import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  FaRobot,
  FaBrain,
  FaFlask,
  FaUsers,
  FaTrophy,
  FaCode,
  FaCheckCircle,
  FaCalendarAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

// ─── Data ────────────────────────────────────────────────────────────────────

const gains = [
  {
    icon: FaCode,
    title: "Real Coding Skills",
    desc: "Children write actual code, not drag-and-drop only. Younger learners start with block-based programming and progress to Python and C++.",
  },
  {
    icon: FaRobot,
    title: "Hands-On Robotics",
    desc: "Every child works with real hardware: microcontrollers, sensors, motors, and chassis. They build, wire, and programme their own robot from scratch.",
  },
  {
    icon: FaBrain,
    title: "Engineering Thinking",
    desc: "Sessions integrate mathematics, physics, and logical reasoning in a context that makes the answer move, turning abstract concepts into lived understanding.",
  },
  {
    icon: FaFlask,
    title: "Scientific Problem-Solving",
    desc: "Learners test hypotheses, iterate on failures, and refine solutions, developing a scientific mindset that transfers across every subject and career.",
  },
  {
    icon: FaUsers,
    title: "Teamwork & Communication",
    desc: "Projects require planning, collaboration, and presenting results. Children develop the soft skills employers and universities value alongside the technical ones.",
  },
  {
    icon: FaTrophy,
    title: "Competition Credentials",
    desc: "Graduates are competition-ready. Results from local, regional, and international robotics competitions strengthen academic portfolios and university applications.",
  },
];

const ageGroups = [
  {
    label: "Explorers",
    ages: "8 – 11",
    color: "bg-blue-500",
    description:
      "Introduction to block-based coding and simple robotic assemblies. Learners build fundamental logic, sequencing, and spatial reasoning through guided, play-based projects.",
    outcomes: [
      "Block-based programming fundamentals",
      "Basic sensor and motor integration",
      "First autonomous robot build",
    ],
  },
  {
    label: "Builders",
    ages: "12 – 14",
    color: "bg-customRed",
    description:
      "Transition from block coding to text-based languages. Learners design more complex robots, work with multiple sensors, and begin competitive programming concepts.",
    outcomes: [
      "Introduction to Python / C++ syntax",
      "Multi-sensor robot control",
      "Local competition preparation",
    ],
  },
  {
    label: "Engineers",
    ages: "15 – 18",
    color: "bg-indigo-600",
    description:
      "Advanced track focused on autonomous systems, algorithm design, and competition strategy. Students build competition-ready robots eligible for regional and international events.",
    outcomes: [
      "Autonomous navigation algorithms",
      "Robotic arm integration (Mobile Manipulator)",
      "Regional and international competition pathway",
    ],
  },
];

const programmeOptions = [
  {
    name: "Core: Mobile Robotics",
    sub: "Explorers · Builders · Engineers",
    duration: "10 – 40 weeks",
    ages: "8 – 18",
    outcome: "Competition-ready autonomous robot",
  },
  {
    name: "Bonus: Robotic Arm Workshop",
    sub: "",
    duration: "10 weeks",
    ages: "14 – 18",
    outcome: "Fully programmed robotic arm",
  },
  {
    name: "Advanced: Mobile Manipulator",
    sub: "Follow-up programme",
    duration: "Follow-up",
    ages: "15 – 18",
    outcome: "Arm and chassis integrated system",
  },
];

const expectItems = [
  "Everything provided: curriculum, equipment, trained instructors, and test tracks",
  "Your child works with real hardware: sensors, microcontrollers, motors, and chassis",
  "Sessions run 1–2 times per week over 10 to 40 weeks depending on the programme",
  "Three age-appropriate tracks for children from 8 to 18 years old",
  "Every child completes a working autonomous robot they built and programmed themselves",
  "Competition-ready graduates with results that strengthen academic and university applications",
];

const competitionPoints = [
  "Local and regional competitions from the very first year",
  "International pathways for students aged 15–18",
  "Competition results bolster academic portfolios and university applications",
  "Your child leaves knowing exactly what they are capable of",
];

const bootcampTracks = [
  {
    label: "Little Coders",
    sub: "Under Age 8",
    price: "R 650",
    accent: "border-yellow-400",
    accentText: "text-yellow-400",
    items: [
      "10 guided sessions",
      "Age-appropriate learning materials",
      "Printed activity materials",
      "Trained instructor",
      "Certificate of completion",
      "Fun parent showcase",
    ],
    build: null,
    note: "No dev kit. Age-appropriate introduction to the basics of robotics and computational thinking.",
  },
  {
    label: "Explorers",
    sub: "Ages 8 to 11",
    price: "R 1,800",
    accent: "border-blue-400",
    accentText: "text-blue-400",
    items: [
      "10 guided sessions",
      "Robotics kit (shared)",
      "All materials & worksheets",
      "Trained instructor",
      "Certificate of completion",
      "Race Day showcase",
    ],
    build: "An autonomous corridor-following robot",
    note: null,
  },
  {
    label: "Builders",
    sub: "Ages 12 to 14",
    price: "R 2,150",
    accent: "border-green-400",
    accentText: "text-green-400",
    items: [
      "10 guided sessions",
      "Robotics kit (shared)",
      "All materials & worksheets",
      "Trained instructor",
      "Certificate of completion",
      "Live parent demonstration",
    ],
    build: "A self-driving maze robot with Ultrasonic and IR sensors",
    note: null,
  },
  {
    label: "Engineers",
    sub: "Ages 15 to 18",
    price: "R 2,750",
    accent: "border-orange-400",
    accentText: "text-orange-400",
    items: [
      "10 guided sessions",
      "Robotics kit (shared)",
      "All materials & worksheets",
      "Trained instructor",
      "Certificate of completion",
      "Timed competition final",
    ],
    build: "A precision speed-optimised line-following robot",
    note: null,
  },
];

const siblingDiscounts = [
  { siblings: "2 Siblings", off: "10% off", desc: "10% off the combined total" },
  { siblings: "3 Siblings", off: "15% off", desc: "15% off the combined total" },
  { siblings: "4 Siblings", off: "20% off", desc: "20% off the combined total" },
];

const howItWorks = [
  { step: "1", title: "Enrol", desc: "Once the next cohort opens, complete the enrolment form and pay upfront or in instalments via Payflex to secure your child's place." },
  { step: "2", title: "Kit Ready", desc: "Your child receives their robotics kit on Day 1. Everything is provided. No extra purchases needed." },
  { step: "3", title: "Build & Code", desc: "Over 10 sessions your child builds, wires, and programmes their robot with a trained instructor." },
  { step: "4", title: "Showcase", desc: "On the final day your child demonstrates their working robot. Every learner receives a certificate." },
];

const faqs = [
  {
    q: "Do I need to buy anything extra?",
    a: "No. The fee covers everything: the robotics kit, all materials, the instructor, and the certificate. No extra purchases needed at any point.",
  },
  {
    q: "What if my child has never coded before?",
    a: "The bootcamp is designed for complete beginners. No prior coding or electronics experience is required. Your child is guided from session one.",
  },
  {
    q: "How many children are in each session?",
    a: "A maximum of 10 learners per session, working in pairs. Small class sizes ensure every learner gets meaningful hands-on time.",
  },
  {
    q: "What if my child misses a session?",
    a: "Please notify us in advance. We will do our best to accommodate catch-up within the same cohort so your child does not fall behind.",
  },
  {
    q: "Can my child continue after the bootcamp?",
    a: "Yes. The bootcamp feeds directly into the full 10 to 40 week programme. Top-up from as little as R 302. Ask us on the final day.",
  },
  {
    q: "What is the refund policy?",
    a: "Full refund for cancellations 7 or more days before the start. Within 7 days: 50% admin fee. No refund once commenced.",
  },
];

const continueTable = [
  { track: "Little Coders", bootcamp: "R 650", full: "N/A", topup: "N/A", change: "Introductory track only" },
  { track: "Explorers", bootcamp: "R 1,800", full: "R 2,102", topup: "R 302", change: "Deeper curriculum and longer build progression" },
  { track: "Builders", bootcamp: "R 2,150", full: "R 2,523", topup: "R 373", change: "Extended sensor work and navigation logic" },
  { track: "Engineers", bootcamp: "R 2,750", full: "R 3,163", topup: "R 413", change: "Full competition pathway and advanced code" },
];

// ─── FAQ Accordion Item ───────────────────────────────────────────────────────
const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="border border-white/10 rounded-xl overflow-hidden cursor-pointer"
      onClick={() => setOpen(!open)}
    >
      <div className="flex items-center justify-between px-5 py-4 bg-[#1e293b] hover:bg-[#253347] transition-colors">
        <p className="font-semibold text-white text-sm pr-4">{q}</p>
        {open ? (
          <FaChevronUp className="text-blue-400 flex-shrink-0" />
        ) : (
          <FaChevronDown className="text-gray-400 flex-shrink-0" />
        )}
      </div>
      {open && (
        <div className="px-5 py-4 bg-[#172033]">
          <p className="text-gray-300 text-sm leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
};

// ─── Component ───────────────────────────────────────────────────────────────

const FutureEngineers = () => {
  const navigate = useNavigate();

  return (
    <div className="bg-[#0d1117] text-white">
      <Helmet>
        <title>Future Engineers Programme | Ikusasa Technology Solutions</title>
        <meta
          name="description"
          content="Ikusasa Technology Solutions delivers the Future Engineers Programme, a hands-on Coding and Robotics programme for ages 8 to 18, developed in partnership with Zurisoft Technologies."
        />
        <link rel="canonical" href="https://ikusasatech.com/future-engineers" />
      </Helmet>

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="relative py-28 px-6 text-center overflow-hidden min-h-[520px] flex items-center">
        {/* background image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/images/fep-hero.png')" }}
        />
        {/* dark overlay */}
        <div className="absolute inset-0 bg-black/65" />

        <div className="relative w-full max-w-3xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-extrabold leading-tight mb-4">
            Future Engineers
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">
              Programme
            </span>
          </h1>
          <p className="text-blue-300 italic text-lg md:text-xl mb-6">
            Equipping young minds to engineer the world ahead.
          </p>
          <p className="text-gray-200 text-base md:text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
            Give your child the skills to build the world ahead, not just live
            in it. A hands-on Coding and Robotics programme for ages 8 to 18,
            delivered by Ikusasa Technology Solutions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://fep-future-engineers.netlify.app/enroll"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-8 py-3 rounded-full transition duration-300 transform hover:scale-105"
            >
              Enrol Your Child
            </a>
            <button
              onClick={() => navigate("/contact")}
              className="border border-white/50 hover:border-white text-white font-semibold px-8 py-3 rounded-full transition duration-300"
            >
              Get in Touch
            </button>
          </div>
        </div>
      </section>

      {/* ── November Cohort Banner ──────────────────────────────────── */}
      <section className="bg-gradient-to-r from-blue-600 to-indigo-700 py-5 px-6">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
          <div className="flex items-center gap-3">
            <FaCalendarAlt className="text-white text-2xl flex-shrink-0" />
            <div>
              <p className="font-extrabold text-white text-lg leading-tight">November Cohort Now Enrolling</p>
              <p className="text-white/80 text-sm">Saturday classes for ages 8 to 18. Secure your child's place today.</p>
            </div>
          </div>
          <a
            href="https://fep-future-engineers.netlify.app/enroll"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 bg-white text-blue-700 font-bold px-6 py-2.5 rounded-full hover:bg-gray-100 transition duration-300 transform hover:scale-105 text-sm"
          >
            Enrol Your Child
          </a>
        </div>
      </section>

      {/* ── Why This Matters ─────────────────────────────────────────── */}
      <section className="py-20 px-6 relative">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-15 pointer-events-none"
          style={{ backgroundImage: "url('/images/fep-bg.png')" }}
        />
        <div className="relative max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold mb-8">
            Why Does This Matter for Your Child?
          </h2>
          <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-6">
            The world your child will graduate into is being built on
            automation, AI, and smart systems. The question is not whether
            technology will shape their future. It is whether they will be the
            ones shaping it. Coding and Robotics education gives them the
            foundation to lead, not just adapt.
          </p>
          <p className="text-gray-300 text-base md:text-lg leading-relaxed">
            Robotics is uniquely powerful because it makes abstract skills
            concrete. In a single session, your child applies mathematics,
            physics, logical thinking, and creative problem-solving, and they
            see the result move. That moment of ownership changes how a young
            person sees themselves.
          </p>
        </div>
      </section>

      {/* ── What Your Child Gains ────────────────────────────────────── */}
      <section className="py-20 px-6 bg-[#0f172a]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-extrabold text-center mb-4">
            What Your Child Gains
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-xl mx-auto">
            Every skill built in this programme has a purpose beyond the
            classroom.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {gains.map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="bg-[#1e293b] rounded-xl p-6 border border-white/5 hover:border-blue-500/40 transition-colors duration-300"
              >
                <Icon className="text-blue-400 text-3xl mb-4" />
                <h3 className="text-white font-bold text-lg mb-2">{title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Holiday Bootcamp ─────────────────────────────────────────── */}
      <section id="holiday-bootcamp" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">

          {/* header */}
          <div className="text-center mb-4">
            <span className="inline-block bg-gray-500/20 text-gray-400 text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full mb-4">
              Enrolments Closed · Next Bootcamp: Summer Holidays
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold mb-3">
              Your child builds a{" "}
              <span className="text-orange-400">real robot.</span>
              <br />In 3 weeks.
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto mb-6">
              Hands-on coding and robotics for learners aged 4 and above. Four age-appropriate tracks, each ending with a working outcome your child built and programmed themselves. All fees include VAT at 15%.
            </p>
            <p className="text-orange-400 text-sm font-semibold mb-6">
              Enrolments for our most recent bootcamp have closed. Our next bootcamp runs over the summer holidays — register your interest below and we'll notify you as soon as applications open.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-xs text-gray-400 mb-10">
              {["3 weeks · 10 sessions", "3 to 4 sessions per week", "2 hours per session", "Max 10 learners", "Learners work in pairs"].map(t => (
                <span key={t} className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-400 inline-block" />
                  {t}
                </span>
              ))}
            </div>
          </div>

          {/* track cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
            {bootcampTracks.map((track) => (
              <div key={track.label} className={`bg-[#1e293b] rounded-xl border-t-4 ${track.accent} flex flex-col`}>
                <div className="p-5 flex-1">
                  <p className={`font-extrabold text-lg ${track.accentText}`}>{track.label}</p>
                  <p className="text-gray-400 text-xs uppercase tracking-wide mb-3">{track.sub}</p>
                  <p className="text-gray-500 text-xs uppercase tracking-widest mb-1">Fee (Next Cohort)</p>
                  <p className="text-white text-3xl font-extrabold mb-1">{track.price}</p>
                  <p className="text-gray-500 text-xs mb-4">incl. VAT · Instalments via Payflex</p>
                  <p className="text-gray-500 text-xs uppercase tracking-widest mb-2">Included</p>
                  <ul className="space-y-1 mb-4">
                    {track.items.map(item => (
                      <li key={item} className="flex items-start gap-2 text-xs text-gray-300">
                        <span className={`mt-1 w-1.5 h-1.5 rounded-full ${track.accent.replace('border-', 'bg-')} flex-shrink-0`} />
                        {item}
                      </li>
                    ))}
                  </ul>
                  {track.build && (
                    <div className="bg-[#0f172a] rounded-lg p-3 mt-auto">
                      <p className="text-gray-500 text-xs">They will build:</p>
                      <p className={`text-xs font-semibold italic ${track.accentText}`}>{track.build}</p>
                    </div>
                  )}
                  {track.note && (
                    <div className="bg-[#0f172a] rounded-lg p-3 mt-auto">
                      <p className={`text-xs italic ${track.accentText}`}>{track.note}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* sibling discounts */}
          <div className="mb-14">
            <h3 className="text-xl font-bold text-center mb-6">Sibling Discount</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {siblingDiscounts.map(({ siblings, off, desc }) => (
                <div key={siblings} className="bg-[#1e293b] rounded-xl p-6 text-center border border-white/5">
                  <p className="text-gray-400 text-xs uppercase tracking-widest mb-1">{siblings}</p>
                  <p className="text-3xl font-extrabold text-blue-400 mb-1">{off}</p>
                  <p className="text-gray-400 text-sm">{desc}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-gray-500 text-xs mt-4">
              Discount applies to the combined bill. Each additional sibling adds 5% more off the combined total.
            </p>
          </div>

          {/* how it works */}
          <div className="mb-14">
            <h3 className="text-xl font-bold text-center mb-8">How It Works</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {howItWorks.map(({ step, title, desc }) => (
                <div key={step} className="bg-[#1e293b] rounded-xl p-5 border border-white/5 relative">
                  <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center font-bold text-sm mb-3">{step}</div>
                  <p className="font-bold text-white mb-1">{title}</p>
                  <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ accordion */}
          <div className="mb-14 max-w-3xl mx-auto">
            <h3 className="text-xl font-bold text-center mb-6">Frequently Asked Questions</h3>
            <div className="space-y-3">
              {faqs.map((faq) => <FaqItem key={faq.q} {...faq} />)}
            </div>
          </div>

          {/* continue to full programme table */}
          <div className="mb-6">
            <h3 className="text-xl font-bold text-center mb-6">
              Love it? Continue into the Full Programme.
            </h3>
            <p className="text-gray-400 text-center text-sm mb-8 max-w-xl mx-auto">
              The bootcamp is a taster. After completing it your child can join the full 10 to 40 week programme. Top-up from as little as R 302.
            </p>
            <div className="overflow-x-auto rounded-xl border border-white/10">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-blue-600 text-white">
                    <th className="text-left py-3 px-4 font-semibold">Track</th>
                    <th className="text-left py-3 px-4 font-semibold">Bootcamp</th>
                    <th className="text-left py-3 px-4 font-semibold">10-Week Programme</th>
                    <th className="text-left py-3 px-4 font-semibold">Top-Up</th>
                    <th className="text-left py-3 px-4 font-semibold hidden md:table-cell">What Changes</th>
                  </tr>
                </thead>
                <tbody>
                  {continueTable.map((row, i) => (
                    <tr key={row.track} className={i % 2 === 0 ? "bg-[#1e293b]" : "bg-[#172033]"}>
                      <td className="py-3 px-4 font-bold text-white">{row.track}</td>
                      <td className="py-3 px-4 text-gray-300">{row.bootcamp}</td>
                      <td className="py-3 px-4 text-gray-300">{row.full}</td>
                      <td className="py-3 px-4 font-bold text-orange-400">{row.topup}</td>
                      <td className="py-3 px-4 text-gray-400 hidden md:table-cell">{row.change}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-gray-500 text-xs mt-3 text-center">All fees include VAT at 15%. A valid VAT invoice is available on request.</p>
          </div>

          {/* bootcamp CTA */}
          <div className="bg-[#1e293b] rounded-2xl p-8 text-center border border-orange-400/20">
            <p className="font-extrabold text-2xl text-white mb-2">Missed the Last Bootcamp?</p>
            <p className="text-gray-400 text-sm mb-6">Our next bootcamp runs over the summer holidays. Register your interest and we'll notify you as soon as applications open.</p>
            <a
              href="https://fep-future-engineers.netlify.app/enroll"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-3 rounded-full transition duration-300 transform hover:scale-105"
            >
              Enquire About the Next Bootcamp
            </a>
          </div>

        </div>
      </section>

      {/* ── Age-Appropriate Learning ─────────────────────────────────── */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4">
              Age-Appropriate Learning for Every Stage
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto">
              The same curriculum framework scales across three age groups,
              ensuring every student is appropriately challenged, never bored,
              never overwhelmed.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {ageGroups.map((group) => (
              <div
                key={group.label}
                className="bg-[#1e293b] rounded-xl overflow-hidden border border-white/5"
              >
                <div className={`${group.color} py-4 px-6`}>
                  <p className="font-extrabold text-xl">{group.label}</p>
                  <p className="text-white/80 text-sm font-medium">
                    Ages {group.ages}
                  </p>
                </div>
                <div className="p-6">
                  <p className="text-gray-300 text-sm leading-relaxed mb-4">
                    {group.description}
                  </p>
                  <ul className="space-y-2">
                    {group.outcomes.map((o) => (
                      <li key={o} className="flex items-start gap-2 text-sm text-gray-400">
                        <FaCheckCircle className="text-blue-400 mt-0.5 flex-shrink-0" />
                        {o}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Programme Options Table ──────────────────────────────────── */}
      <section className="py-20 px-6 bg-[#0f172a]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4">
              Programme Options
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto">
              Flexible programme lengths to suit your schedule, from a 10-week
              introductory module to a full 40-week academic year. All
              programmes include a personal electronics kit, trained
              instructors, curriculum materials, test tracks, and full
              facilitation.
            </p>
          </div>
          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-blue-600 text-white">
                  <th className="text-left py-4 px-5 font-semibold">Programme</th>
                  <th className="text-left py-4 px-5 font-semibold">Duration</th>
                  <th className="text-left py-4 px-5 font-semibold">Ages</th>
                  <th className="text-left py-4 px-5 font-semibold">Student Outcome</th>
                </tr>
              </thead>
              <tbody>
                {programmeOptions.map((row, i) => (
                  <tr
                    key={row.name}
                    className={i % 2 === 0 ? "bg-[#1e293b]" : "bg-[#172033]"}
                  >
                    <td className="py-4 px-5">
                      <p className="font-bold text-white">{row.name}</p>
                      {row.sub && (
                        <p className="text-gray-400 text-xs mt-0.5">{row.sub}</p>
                      )}
                    </td>
                    <td className="py-4 px-5 text-gray-300">{row.duration}</td>
                    <td className="py-4 px-5 text-gray-300">{row.ages}</td>
                    <td className="py-4 px-5 text-gray-300">{row.outcome}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── What to Expect ───────────────────────────────────────────── */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-extrabold mb-4">
            What to Expect
          </h2>
          <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-8">
            Every session is hands-on from start to finish. Your child does not
            watch. They do. The programme runs through schools, after-school
            programmes, and community organisations. Everything is handled:
            curriculum, materials, instructors, and equipment.{" "}
            <span className="text-white font-semibold">
              Your child shows up ready to learn. We handle the rest.
            </span>
          </p>
          <ul className="space-y-4">
            {expectItems.map((item) => (
              <li key={item} className="flex items-start gap-3">
                <span className="mt-1 w-2.5 h-2.5 rounded-full bg-blue-500 flex-shrink-0" />
                <span className="text-gray-300 text-base">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── LinkedIn Video ───────────────────────────────────────────── */}
      <section className="py-20 px-6 bg-[#0f172a]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold mb-4">
            See It in Action
          </h2>
          <p className="text-gray-400 mb-10 max-w-xl mx-auto">
            Watch real students from the programme build, programme, and compete
            with their own robots.
          </p>
          <div className="flex flex-col md:flex-row justify-center gap-6 items-start">
            <iframe
              src="https://www.linkedin.com/embed/feed/update/urn:li:activity:7440353270612520960?compact=1"
              height="399"
              width="504"
              frameBorder="0"
              allowFullScreen
              title="Future Engineers Programme - Post"
              className="rounded-xl max-w-full"
            />
            <iframe
              src="https://www.linkedin.com/embed/feed/update/urn:li:ugcPost:7447632003417686017?compact=1"
              height="399"
              width="504"
              frameBorder="0"
              allowFullScreen
              title="Future Engineers Programme - Video"
              className="rounded-xl max-w-full"
            />
          </div>
        </div>
      </section>

      {/* ── Competition Pathway ──────────────────────────────────────── */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="border border-white/10 rounded-2xl p-8 md:p-12">
            <p className="text-blue-400 text-xs font-bold tracking-widest uppercase mb-3">
              Competition Pathway
            </p>
            <h2 className="text-2xl md:text-3xl font-extrabold mb-4">
              A Clear Pathway to Competitions
            </h2>
            <p className="text-gray-400 text-base leading-relaxed mb-6">
              Every student graduates with a working robot they can enter into
              competition. The 40-week programme specifically builds toward
              competition-ready performance, with final units dedicated to
              testing, refinement, and competition strategy.
            </p>
            <ul className="space-y-3">
              {competitionPoints.map((point) => (
                <li key={point} className="flex items-start gap-3">
                  <span className="mt-1.5 w-2 h-2 rounded-full bg-yellow-400 flex-shrink-0" />
                  <span className="text-gray-300 text-base">{point}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────── */}
      <section className="py-24 px-6 bg-gradient-to-br from-blue-600 to-indigo-700 text-center">
        <h2 className="text-3xl md:text-4xl font-extrabold mb-4">
          Give your child that chance.
        </h2>
        <p className="text-white/80 text-base md:text-lg max-w-xl mx-auto mb-10 leading-relaxed">
          Every child deserves the opportunity to discover what they are capable
          of. Enrol your child today or get in touch to find out which track is
          right for them.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
          <a
            href="https://fep-future-engineers.netlify.app/enroll"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-blue-700 font-bold px-8 py-3 rounded-full hover:bg-gray-100 transition duration-300 transform hover:scale-105"
          >
            Enrol Your Child
          </a>
          <button
            onClick={() => navigate("/contact")}
            className="border-2 border-white text-white font-semibold px-8 py-3 rounded-full hover:bg-white/10 transition duration-300"
          >
            Contact Us
          </button>
        </div>
      </section>
    </div>
  );
};

export default FutureEngineers;
